import { MutableRefObject, ReactNode, useEffect, useState } from "react";
import { FieldErrors, FieldValues, Path, PathValue, RegisterOptions, UseFormRegister, UseFormReturn } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import CurrencyDollar2Icon from "../../icons/currencyDollar2.icon";
import { inputCss, selectCss, textareaCss } from "./native.comp";
import EyeIcon from "../../icons/eye.icon";
import EyeSlashIcon from "../../icons/eyeSlash.icon";
import React from "react";

export enum EditingMode {
  create,
  edit,
}

const arrayRegex = /^([^\[]+)\[(\d+)\]$/;

export function LabelAndInput<T extends FieldValues>({
  register,
  required,
  label,
  fieldName,
  type = "text",
  registerOptions,
  labelCss = "mb-2",
  topLabelCss = "mb-2",
  errors,
  step,
  editable = true,
  infoComponents,
  id,
}: {
  register: UseFormRegister<T>;
  label: string;
  fieldName: string;
  type?: string;
  registerOptions?: RegisterOptions<T, any>;
  labelCss?: string;
  topLabelCss?: string;
  errors?: FieldErrors<T>;
  step?: string;
  required?: boolean;
  editable?: boolean;
  infoComponents?: React.ReactNode;
  id?: string;
}) {
  let errorCss = "";

  const err = isError(errors, fieldName);
  if (err !== null) {
    errorCss = "border-red-400 focus:border-red-400 focus-visible:border-red-400 hover:border-red-400";
  }

  const labelText = required ? `${label} *` : label;

  return (
    <label className={topLabelCss}>
      <div className={`flex items-center gap-2 ${labelCss}`}>
        <p>{labelText}</p>
        {infoComponents}
      </div>
      <FancyTextInput
        id={id}
        type={type}
        register={() => register(fieldName as any, registerOptions)}
        placeholder={label}
        className={errorCss}
        step={step}
        required={required}
        editable={editable}
      />
      {err && err.message && <span className="ml-2 text-xs text-red-400">{err.message}</span>}{" "}
    </label>
  );
}

export function LabelAndTextArea<T extends FieldValues>({
  register,
  label,
  fieldName,
  registerOptions,
  rows = 10,
  labelCss,
  topLabelCss = "block mt-4",
  errors,
}: {
  register: UseFormRegister<T>;
  label: string;
  fieldName: string;
  registerOptions?: RegisterOptions<T, any>;
  rows?: number;
  labelCss?: string;
  topLabelCss?: string;
  errors?: FieldErrors<T>;
}) {
  let errorCss = "";

  const err = isError(errors, fieldName);
  if (err !== null) {
    errorCss = "border-red-400";
  }

  const labelText = registerOptions && registerOptions.required ? `${label} *` : label;

  return (
    <label className={topLabelCss}>
      <p className={`${labelCss}`}>
        {labelText} {err && err.message && <span className="ml-2 text-xs text-red-400">{err.message}</span>}
      </p>

      <FancyTextArea
        register={() => register(fieldName as any, registerOptions)}
        placeholder={label}
        rows={rows}
        className={errorCss}
      />
    </label>
  );
}

export const fancyCheckboxCss =
  "mr-2 " +
  "cursor-pointer " +
  "appearance-none " + // hide the original checkbox
  "before:h-4 before:w-4 before:border before:hover:border-2 before:border-gray-400 before:block before:opactiy-100 before:rounded " + // style when not checked
  "before:hover:border-blue-600 " + // change broder when hover.  make sure no transition duration is applied
  "before:checked:bg-blue-600 before:checked:border-0 " + // style when checked
  "before:transition-[background-color] before:duration-600"; // transition effect when checked

export function FancyCheckbox({
  labelId,
  value,
  defaultChecked,
  register,
  children,
}: {
  labelId?: string;
  value?: string | boolean;
  defaultChecked?: string | boolean;
  register: any;
  children: React.ReactNode;
}) {
  const labelCss =
    "flex items-center " + // seems like this is the only way I can get both the checkbox and label vertically aligned
    "hover:font-semibold hover:text-blue-600"; // style when hover

  return (
    <label id={labelId} className={labelCss}>
      <input className={fancyCheckboxCss} type="checkbox" defaultChecked={defaultChecked} value={value} {...register} />
      {children}
    </label>
  );
}

export function FancyRadiobox({
  labelId,
  value,
  name,
  defaultChecked,
  register,
  children,
}: {
  labelId?: string;
  value: string | boolean;
  name?: string;
  defaultChecked?: string | boolean;
  register: any;
  children: React.ReactNode;
}) {
  const inputCss =
    "mr-2 " +
    "appearance-none " + // hide the original radio
    "before:h-4 before:w-4 before:border before:hover:border-2 before:border-gray-400 before:block before:opactiy-100 before:rounded " + // style when not checked
    "before:hover:border-blue-600 " + // change broder when hover.  make sure no transition duration is applied
    "before:checked:bg-blue-600 before:checked:border-0 " + // style when checked
    "before:transition-[background-color] before:duration-600"; // transition effect when checked

  const labelCss =
    "flex items-center " + // seems like this is the only way I can get both the radio and label vertically aligned
    "cursor-pointer " +
    "hover:font-semibold hover:text-blue-600"; // style when hover

  return (
    <label id={labelId} className={labelCss}>
      <input className={inputCss} type="radio" name={name} defaultChecked={defaultChecked} value={value} {...register} />
      {children}
    </label>
  );
}

export function FancyRadiobox2({ labelId, register, children }: { labelId?: string; register: any; children: React.ReactNode }) {
  const inputCss =
    "mr-2 " +
    "appearance-none " + // hide the original radio
    "before:h-4 before:w-4 before:border before:hover:border-2 before:border-gray-400 before:block before:opactiy-100 before:rounded " + // style when not checked
    "before:hover:border-blue-600 " + // change broder when hover.  make sure no transition duration is applied
    "before:checked:bg-blue-600 before:checked:border-0 " + // style when checked
    "before:transition-[background-color] before:duration-600"; // transition effect when checked

  const labelCss =
    "flex items-center " + // seems like this is the only way I can get both the radio and label vertically aligned
    "cursor-pointer " +
    "hover:font-semibold hover:text-blue-600"; // style when hover

  return (
    <label id={labelId} className={labelCss}>
      <input className={inputCss} type="radio" {...register} />
      {children}
    </label>
  );
}

export function FancyYesNoRadioGroup<T extends FieldValues>({ name, methods }: { name: Path<T>; methods: UseFormReturn<T> }) {
  const inputCss =
    "mr-2 " +
    "appearance-none " +
    "before:h-4 before:w-4 before:border before:hover:border-2 before:border-gray-400 before:block before:opactiy-100 before:rounded-full " +
    "before:hover:border-blue-600 " +
    "before:checked:bg-blue-600 before:checked:border-0 " +
    "before:transition-[background-color] before:duration-600";

  const labelCss = "flex items-center " + "cursor-pointer " + "hover:font-semibold hover:text-blue-600";

  const currentValue = methods.watch(name);

  function handleChange(value: string) {
    let formValue: undefined | boolean;
    if (value === "") {
      formValue = undefined;
    } else if (value === "yes") {
      formValue = true;
    } else {
      formValue = false;
    }
    methods.setValue(name, formValue as any, { shouldValidate: true, shouldDirty: true });
  }

  return (
    <div className="flex gap-4">
      <label className={labelCss}>
        <input
          className={inputCss}
          type="radio"
          name={name}
          value=""
          checked={currentValue === undefined}
          onChange={() => handleChange("")}
        />
        Not Selected
      </label>
      <label className={labelCss}>
        <input
          className={inputCss}
          type="radio"
          name={name}
          value="yes"
          checked={currentValue === true}
          onChange={() => handleChange("yes")}
        />
        Yes
      </label>
      <label className={labelCss}>
        <input
          className={inputCss}
          type="radio"
          name={name}
          value="no"
          checked={currentValue === false}
          onChange={() => handleChange("no")}
        />
        No
      </label>
    </div>
  );
}

export function FancyTextInput({
  register,
  placeholder,
  type = "text",
  step,
  className,
  required,
  inputRef,
  editable = true,
  id,
}: {
  register: any;
  placeholder?: string;
  type?: string;
  step?: string;
  className?: string;
  required?: boolean;
  inputRef?: MutableRefObject<HTMLDivElement | undefined>;
  editable?: boolean; // if false, the input will be read-only
  id?: string;
}) {
  if (type === "password") {
    return (
      <FancyTextInputPassword
        register={register}
        placeholder={placeholder}
        step={step}
        className={className}
        required={required}
        inputRef={inputRef}
      />
    );
  }

  return (
    <input
      id={id}
      className={`${inputCss} ${className ?? ""} w-full my-0 px-4 h-10 outline-none `}
      type={type}
      step={step}
      {...register({
        ref: inputRef,
      })}
      placeholder={placeholder}
      required={required}
      readOnly={!editable} // if editable is false, the input will be read-only
    />
  );
}

export function FancyTextInputPassword({
  register,
  placeholder,
  step,
  className,
  required,
  inputRef,
}: {
  register: any;
  placeholder?: string;
  type?: string;
  step?: string;
  className?: string;
  required?: boolean;
  inputRef?: MutableRefObject<HTMLDivElement | undefined>;
}) {
  const [type, setType] = useState("password");

  function onClickRightIcon() {
    setType(type === "password" ? "text" : "password");
  }

  return (
    <div className={`relative ${className}`}>
      <input
        className={`${inputCss} ${className ?? ""} w-full my-0 px-4 h-10 outline-none `}
        type={type}
        step={step}
        {...register({
          ref: inputRef,
        })}
        placeholder={placeholder}
        required={required}
      />
      <button onClick={onClickRightIcon} className="absolute top-1/2 -translate-y-1/2 right-2" type="button">
        {type === "password" && <EyeIcon />}
        {type === "text" && <EyeSlashIcon />}
      </button>
    </div>
  );
}

export function FancyTextInputDollarSign({ register }: { register: any }) {
  return (
    <div className="relative">
      <CurrencyDollar2Icon className="absolute top-[13.5px] left-1 w-6 h-6" />
      <FancyTextInput register={register} className="pl-6" type="number" />
    </div>
  );
}

export function FancyTextInputWithIcon({
  register,
  placeholder,
  type = "text",
  step,
  icon,
  rightIcon,
  rightIconRole,
  onClickRightIcon,
  className,
  inputRef,
}: {
  register: any;
  placeholder?: string;
  type?: string;
  step?: string;
  icon: React.ReactNode;
  rightIcon?: React.ReactNode;
  rightIconRole?: string;
  onClickRightIcon?: () => void;
  className?: string;
  inputRef?: MutableRefObject<HTMLDivElement | undefined>;
}) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute top-1/2 -translate-y-1/2 left-2">{icon}</div>
      <FancyTextInput
        className="indent-8"
        register={register}
        placeholder={placeholder}
        type={type}
        step={step}
        inputRef={inputRef}
      />
      <button role={rightIconRole} onClick={onClickRightIcon} className="absolute top-1/2 -translate-y-1/2 right-2" type="button">
        {rightIcon}
      </button>
    </div>
  );
}

export function FancyTextArea({
  register,
  placeholder,
  rows,
  className,
}: {
  register: any;
  placeholder?: string;
  rows?: number;
  className?: string;
}) {
  return <textarea className={`${textareaCss} ${className ?? ""}`} rows={rows} {...register()} placeholder={placeholder} />;
}

export function LabelAndSelect<T extends FieldValues>({
  methods,
  label,
  fieldName,
  registerOptions,
  options,
  setDefault = false,
  defaultValue,
  topLabelCss = "block mt-4 w-full",
  selectCss = "w-full",
  selectDataTestId,
}: {
  methods: UseFormReturn<T>;
  label: string;
  fieldName: Path<T>;
  type?: string;
  registerOptions?: RegisterOptions<T, any>;
  options: OptionItem[];
  setDefault?: boolean;
  defaultValue?: PathValue<T, Path<T>>;
  topLabelCss?: string;
  selectCss?: string;
  selectDataTestId?: string;
}) {
  useEffect(() => {
    if (!setDefault) return;

    if (defaultValue) {
      methods.setValue(fieldName, defaultValue);
    } else if (options && options.length > 0) {
      methods.setValue(fieldName, options[0].value as any);
    } else {
      // no idea how to do it right in typescript
      methods.setValue(fieldName, null as any);
    }
    // eslint-disable-next-line
  }, [options]);

  return (
    <label className={topLabelCss}>
      <span className="block">{label}</span>
      <FancySelect
        className={selectCss}
        register={methods.register}
        fieldName={fieldName}
        registerOptions={registerOptions}
        options={options}
        data-role={selectDataTestId}
      />
    </label>
  );
}

export function FancyMinMaxInput<T extends FieldValues>({
  methods,
  title,
  fieldMin,
  fieldMax,
}: {
  methods: UseFormReturn<T>;
  title: string;
  fieldMin: Path<T>;
  fieldMax: Path<T>;
}) {
  const { t, msg } = useTranslation();

  return (
    <div>
      <div>{title}</div>
      <div className="flex gap-4 w-56">
        <label>
          {t(msg.form.min)}: <FancyTextInput register={() => methods.register(fieldMin)} type="number" />
        </label>
        <label>
          {t(msg.form.max)}: <FancyTextInput register={() => methods.register(fieldMax)} type="number" />
        </label>
      </div>
    </div>
  );
}

export interface OptionItem {
  label: string;
  value: string | number | string[];
  disabled?: boolean;
}

export function FancySelect<T extends FieldValues>({
  register,
  fieldName,
  registerOptions,
  options,
  className,
  ...restProps
}: {
  register: UseFormRegister<T>;
  fieldName: Path<T>;
  registerOptions?: RegisterOptions<T, any>;
  options: OptionItem[];
  className?: string;
}) {
  return (
    <select {...register(fieldName)} {...restProps} className={`${selectCss} ${className}`}>
      {options?.map((o, i) => (
        <option key={i} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// export function Selector<T>({
//  methods,
//  label,
//  fieldName,
//  options,
// }: {
//  methods: UseFormReturn<T, any>;
//  label: string;
//  fieldName: Path<T>;
//  options: { name: string | number; value: string | number }[];
// }) {
//  return (
//   <div className="mb-1 rounded-md">
//    <label className="block mb-2">{label}</label>
//    <select {...methods.register(fieldName)} className="px-6 py-1">
//     {options.map((o) => (
//      <option key={o.name} value={o.value}>
//       {o.name}
//      </option>
//     ))}
//    </select>
//   </div>
//  );
// }

function isError(errors: FieldErrors | undefined, fieldName: string) {
  if (!errors) return null;
  // is array

  if (arrayRegex.test(fieldName)) {
    const [, field, idx] = fieldName.match(arrayRegex)!;
    if (!errors[field]) return null;

    const error = errors[field]![idx];
    if (!error) return null;

    return { message: error.message };
  }

  if (errors[fieldName]) {
    return errors[fieldName];
  }

  return null;
}
